import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Edit, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';

const QuoteConfirm = () => {
  const navigate = useNavigate();
  const [quoteData, setQuoteData] = useState(null);
  const [contactData, setContactData] = useState({
    name: '',
    mobile: '',
    email: '',
    agreeTerms: false,
    marketingOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const storedData = sessionStorage.getItem('quoteData');
    if (!storedData) {
      navigate('/quote');
      return;
    }
    setQuoteData(JSON.parse(storedData));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactData.agreeTerms) {
      toast.error('Please agree to the Terms & Privacy Policy');
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        ...quoteData,
        ...contactData
      };

      let response;
      if (quoteData.serviceType === 'delivery') {
        response = await apiService.submitDeliveryQuote(submissionData);
      } else if (quoteData.serviceType === 'mini-move') {
        response = await apiService.submitMiniMoveQuote(submissionData);
      } else if (quoteData.serviceType === 'business') {
        response = await apiService.submitBusinessApplication(submissionData);
      }

      // Store reference ID for thank you page
      sessionStorage.setItem('quoteRef', response.quote_id || response.application_id);
      sessionStorage.setItem('contactMobile', contactData.mobile);
      
      navigate('/quote/thank-you');
    } catch (error) {
      toast.error('Failed to submit. Please try WhatsApp instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quoteData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Indicator */}
      <div className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="ml-2 text-sm text-[#039fff]">Details</span>
            </div>
            <div className="h-px w-12 bg-[#039fff]"></div>
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">2</div>
              <span className="ml-2 text-sm font-medium text-[#039fff]">Confirm</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-gray-600">Done</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
              <CheckCircle className="h-12 w-12 text-[#039fff] mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-[#001A47] mb-2">Confirm Your Details</h1>
              <p className="text-gray-600">Review and add your contact information</p>
            </div>

            {/* Quote Summary */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#001A47]">Your Quote Request</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium text-[#001A47] capitalize">{quoteData.serviceType}</span>
                  </div>
                  
                  {quoteData.pickup && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pickup:</span>
                      <span className="font-medium text-[#001A47]">{quoteData.pickup}</span>
                    </div>
                  )}
                  
                  {quoteData.dropoff && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Drop-off:</span>
                      <span className="font-medium text-[#001A47]">{quoteData.dropoff}</span>
                    </div>
                  )}
                  
                  {quoteData.item && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Item:</span>
                      <span className="font-medium text-[#001A47]">{quoteData.item}</span>
                    </div>
                  )}
                  
                  {quoteData.items && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-medium text-[#001A47]">{quoteData.items.substring(0, 50)}...</span>
                    </div>
                  )}
                  
                  {quoteData.businessName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Business:</span>
                      <span className="font-medium text-[#001A47]">{quoteData.businessName}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={contactData.name}
                      onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={contactData.mobile}
                      onChange={(e) => setContactData({ ...contactData, mobile: e.target.value })}
                      placeholder="+27"
                      required
                    />
                  </div>

                  {(quoteData.serviceType === 'business' || contactData.email) && (
                    <div>
                      <Label htmlFor="email">Email Address {quoteData.serviceType === 'business' && '*'}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        placeholder="your@email.com"
                        required={quoteData.serviceType === 'business'}
                      />
                    </div>
                  )}

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="agreeTerms"
                        checked={contactData.agreeTerms}
                        onChange={(e) => setContactData({ ...contactData, agreeTerms: e.target.checked })}
                        className="mt-1 rounded"
                        required
                      />
                      <Label htmlFor="agreeTerms" className="cursor-pointer text-sm">
                        I agree to the <a href="/terms" className="text-[#039fff] hover:underline">Terms & Conditions</a> and <a href="/privacy" className="text-[#039fff] hover:underline">Privacy Policy</a>
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="marketingOptIn"
                        checked={contactData.marketingOptIn}
                        onChange={(e) => setContactData({ ...contactData, marketingOptIn: e.target.checked })}
                        className="mt-1 rounded"
                      />
                      <Label htmlFor="marketingOptIn" className="cursor-pointer text-sm text-gray-600">
                        Send me occasional specials on WhatsApp (optional)
                      </Label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#fb6402] hover:bg-[#e55a02] text-white py-6 text-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit & Continue on WhatsApp'}
                    <MessageCircle className="ml-2 h-5 w-5" />
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    By submitting, your details will be saved and you'll be redirected to WhatsApp
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteConfirm;
